import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Popover
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as RegisterIcon, PersonAdd as PersonAddIcon, Search as SearchIcon, Close as CloseIcon, Euro as EuroIcon, Visibility as VisibilityIcon, FilterList as FilterListIcon, UploadFile as UploadFileIcon, Inventory2 as Inventory2Icon } from '@mui/icons-material';
import { format, parseISO, startOfDay, isAfter, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../utils/api';
import { parseFatturaPAXml } from '../utils/parseFatturaPAXml';
import SupplierDialog from '../components/accounting/SupplierDialog';
import NewProductFromInvoiceDialog from '../components/accounting/NewProductFromInvoiceDialog';

const DOCUMENT_TYPES = ['Fattura', 'DDT', 'Nota di credito', 'Altro'];
const DEFAULT_IVA = 22;

const AccountingPurchaseInvoicesPage = () => {
  const getListStatus = (inv) => {
    // Regole richieste per elenco:
    // - bozza
    // - pagata
    // - da pagare (non pagata e non è ancora arrivato il giorno della scadenza)
    // - insoluta (non pagata e giorno scadenza superato o vuoto)
    if ((inv.status || '').toLowerCase() === 'bozza') return 'bozza';
    if (inv.paid === true) return 'pagata';

    const dueStr = inv.dueDate ? String(inv.dueDate).slice(0, 10) : '';
    const due = dueStr ? parseISO(dueStr) : null;
    if (!due || !isValid(due)) return 'insoluta';

    const today = startOfDay(new Date());
    return isAfter(due, today) ? 'da pagare' : 'insoluta';
  };

  const getListStatusChip = (inv) => {
    const s = getListStatus(inv);
    switch (s) {
      case 'bozza':
        return { label: 'Bozza', color: 'default' };
      case 'pagata':
        return { label: 'Pagata', color: 'success' };
      case 'da pagare':
        return { label: 'Da pagare', color: 'warning' };
      case 'insoluta':
      default:
        return { label: 'Insoluta', color: 'error' };
    }
  };

  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [invoiceToPay, setInvoiceToPay] = useState(null);
  const [payForm, setPayForm] = useState({ paymentDate: '', paymentMethodId: '', accountId: '' });
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [supplierDialogInitialData, setSupplierDialogInitialData] = useState(null);
  const [openNewProductDialog, setOpenNewProductDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewInvoiceDetail, setViewInvoiceDetail] = useState(null);
  const [wizardError, setWizardError] = useState(null);
  const [listFilters, setListFilters] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    documentType: '',
    supplierName: '',
    totalAmount: '',
    status: ''
  });
  const [orderBy, setOrderBy] = useState('invoiceDate');
  const [order, setOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const [formData, setFormData] = useState({
    supplierId: '',
    invoiceNumber: '',
    invoiceDate: format(new Date(), 'yyyy-MM-dd'),
    documentType: 'Fattura',
    dueDate: '',
    paid: false,
    paymentDate: '',
    ivaPercent: DEFAULT_IVA,
    paymentMethodId: '',
    accountId: '',
    notes: '',
    skipWarehouseMovement: false,
    saveAsBozza: false
  });
  const [items, setItems] = useState([]);
  const [productSearchCode, setProductSearchCode] = useState('');
  const [productSearchDesc, setProductSearchDesc] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchingRowIndex, setProductSearchingRowIndex] = useState(null);
  const [productSearchMode, setProductSearchMode] = useState(null);
  const [pendingMissingProductIndex, setPendingMissingProductIndex] = useState(null);
  const [pendingSaveAfterProducts, setPendingSaveAfterProducts] = useState(false);
  const [priceConflictOnRegister, setPriceConflictOnRegister] = useState(null);
  const [priceConflictLoading, setPriceConflictLoading] = useState(false);
  const [priceConflictSellingStep, setPriceConflictSellingStep] = useState(null);
  const [editableSellingPriceInDialog, setEditableSellingPriceInDialog] = useState('');
  const [activeWizardStep, setActiveWizardStep] = useState(0);
  const [importXmlSuccess, setImportXmlSuccess] = useState(false);
  const [openPendingImportDialog, setOpenPendingImportDialog] = useState(false);
  const [pendingItemsList, setPendingItemsList] = useState([]);
  const [pendingImportLoading, setPendingImportLoading] = useState(false);
  const [pendingImportSelections, setPendingImportSelections] = useState({});
  const codeFieldAnchorRef = useRef(null);
  const descFieldAnchorRef = useRef(null);
  const popoverContentRef = useRef(null);
  const fileInputRef = useRef(null);

  const WIZARD_STEPS = ['Fornitore', 'Documento', 'Articoli'];

  const fetchInvoices = async () => {
    try {
      const res = await api.get('/purchase-invoices');
      setInvoices(res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore caricamento fatture');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPaymentMethodsAndAccounts = async () => {
    try {
      const [pmRes, accRes] = await Promise.all([
        api.get('/payment-methods'),
        api.get('/accounts')
      ]);
      setPaymentMethods(pmRes.data);
      setAccounts(accRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchInvoices(), fetchPaymentMethodsAndAccounts(), fetchSuppliers()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (openDialog && suppliers.length === 0) fetchSuppliers();
  }, [openDialog, suppliers.length]);

  const handleRequestSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const displayedInvoices = useMemo(() => {
    let list = invoices;
    const f = listFilters;
    if (f.invoiceNumber.trim()) {
      const q = f.invoiceNumber.trim().toLowerCase();
      list = list.filter((inv) => (inv.invoiceNumber || '').toLowerCase().includes(q));
    }
    if (f.invoiceDate.trim()) {
      const q = f.invoiceDate.trim().toLowerCase();
      list = list.filter((inv) => {
        const d = inv.invoiceDate ? format(parseISO(inv.invoiceDate), 'dd/MM/yyyy', { locale: it }) : '';
        return d.toLowerCase().includes(q);
      });
    }
    if (f.documentType.trim()) {
      const q = f.documentType.trim().toLowerCase();
      list = list.filter((inv) => (inv.documentType || '').toLowerCase().includes(q));
    }
    if (f.supplierName.trim()) {
      const q = f.supplierName.trim().toLowerCase();
      list = list.filter((inv) => (inv.Supplier?.name || '').toLowerCase().includes(q));
    }
    if (f.totalAmount.trim()) {
      const q = f.totalAmount.trim().replace(',', '.');
      const num = parseFloat(q);
      if (!Number.isNaN(num)) {
        list = list.filter((inv) => parseFloat(inv.totalAmount || 0) >= num);
      } else {
        const str = f.totalAmount.trim().toLowerCase();
        list = list.filter((inv) => String(inv.totalAmount || '').toLowerCase().includes(str));
      }
    }
    if (f.status.trim()) {
      const q = f.status.trim().toLowerCase();
      list = list.filter((inv) => getListStatus(inv).toLowerCase() === q);
    }
    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (orderBy) {
        case 'invoiceNumber':
          cmp = (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '', undefined, { numeric: true });
          break;
        case 'invoiceDate': {
          const da = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
          const db = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
          cmp = da - db;
          break;
        }
        case 'documentType':
          cmp = (a.documentType || '').localeCompare(b.documentType || '');
          break;
        case 'supplierName':
          cmp = (a.Supplier?.name || '').localeCompare(b.Supplier?.name || '');
          break;
        case 'totalAmount':
          cmp = parseFloat(a.totalAmount || 0) - parseFloat(b.totalAmount || 0);
          break;
        case 'status':
          cmp = getListStatus(a).localeCompare(getListStatus(b), 'it', { sensitivity: 'base' });
          break;
        default:
          break;
      }
      return order === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [invoices, listFilters, orderBy, order]);

  const handleOpenNew = () => {
    setEditingInvoice(null);
    setFormData({
      supplierId: '',
      invoiceNumber: '',
      invoiceDate: format(new Date(), 'yyyy-MM-dd'),
      documentType: 'Fattura',
      dueDate: '',
      paid: false,
      paymentDate: '',
      ivaPercent: DEFAULT_IVA,
      paymentMethodId: '',
      accountId: '',
      notes: '',
      skipWarehouseMovement: false,
      saveAsBozza: false
    });
  setItems([{ productId: null, productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, ivaPercent: DEFAULT_IVA, total: 0, isGeneralExpense: false }]);
    setSupplierSearch('');
    setActiveWizardStep(0);
    setWizardError(null);
    setImportXmlSuccess(false);
    setOpenDialog(true);
  };

  const handleOpenEdit = (inv) => {
    setEditingInvoice(inv);
    const invDate = inv.invoiceDate ? (inv.invoiceDate.slice ? inv.invoiceDate.slice(0, 10) : inv.invoiceDate) : '';
    setFormData({
      supplierId: inv.supplierId,
      invoiceNumber: inv.invoiceNumber,
      invoiceDate: invDate,
      documentType: inv.documentType || 'Fattura',
      dueDate: inv.dueDate ? (inv.dueDate.slice ? inv.dueDate.slice(0, 10) : inv.dueDate) : '',
      paid: inv.paid === true,
      paymentDate: inv.paymentDate ? (inv.paymentDate.slice ? inv.paymentDate.slice(0, 10) : inv.paymentDate) : '',
      ivaPercent: inv.ivaPercent != null ? parseFloat(inv.ivaPercent) : DEFAULT_IVA,
      paymentMethodId: inv.paymentMethodId || '',
      accountId: inv.accountId || '',
      notes: inv.notes || '',
      skipWarehouseMovement: inv.skipWarehouseMovement === true,
      saveAsBozza: inv.status === 'bozza'
    });
    const invIva = inv.ivaPercent != null && !Number.isNaN(parseFloat(inv.ivaPercent)) ? parseFloat(inv.ivaPercent) : DEFAULT_IVA;
    const lineItems = (inv.PurchaseInvoiceItems || []).map((i) => {
      const qty = parseFloat(i.quantity) || 0;
      const up = parseFloat(i.unitPrice) || 0;
      const disc = parseFloat(i.discountPercent) || 0;
      const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
      const rowIva = (i.ivaPercent != null && i.ivaPercent !== '' && !Number.isNaN(parseFloat(i.ivaPercent))) ? parseFloat(i.ivaPercent) : invIva;
      return {
        productId: i.productId,
        productCode: i.productCode || (i.Product ? i.Product.code : ''),
        description: i.description || (i.Product ? i.Product.name : ''),
        quantity: qty,
        unitPrice: up,
        discountPercent: disc,
        ivaPercent: rowIva,
        total: imp,
        isGeneralExpense: i.isGeneralExpense === true,
        pendingOrderId: i.pendingOrderId || null,
        pendingOrderItemCode: i.pendingOrderItemCode || null
      };
    });
    setItems(lineItems.length ? lineItems : [{ productId: null, productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, ivaPercent: DEFAULT_IVA, total: 0, isGeneralExpense: false }]);
    setSupplierSearch(inv.Supplier ? inv.Supplier.name : '');
    setActiveWizardStep(0);
    setWizardError(null);
    setImportXmlSuccess(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInvoice(null);
    setActiveWizardStep(0);
    setWizardError(null);
    setImportXmlSuccess(false);
    setProductSearchResults([]);
    setProductSearchingRowIndex(null);
    setOpenNewProductDialog(false);
    setPendingMissingProductIndex(null);
    setPendingSaveAfterProducts(false);
    setPriceConflictOnRegister(null);
    setPriceConflictSellingStep(null);
    setOpenPendingImportDialog(false);
    setPendingItemsList([]);
    setPendingImportSelections({});
  };

  const applyParsedInvoice = (supplier, doc, parsedItems) => {
    const matched = suppliers.find(
      (s) =>
        (supplier.vatNumber && s.vatNumber && String(s.vatNumber).trim() === String(supplier.vatNumber).trim()) ||
        (supplier.fiscalCode && s.fiscalCode && String(s.fiscalCode).trim() === String(supplier.fiscalCode).trim())
    );
    setFormData((prev) => ({
      ...prev,
      supplierId: matched ? matched.id : prev.supplierId,
      invoiceNumber: doc.invoiceNumber || prev.invoiceNumber,
      invoiceDate: doc.invoiceDate || prev.invoiceDate,
      documentType: doc.documentType || prev.documentType,
      dueDate: doc.dueDate || prev.dueDate
    }));
    setSupplierSearch(supplier.name || '');
    if (matched) {
      setSupplierSearchResults([matched]);
      setSupplierDialogInitialData(null);
    } else {
      setSupplierSearchResults([]);
      const dialogData = {
        name: supplier.name || '',
        vatNumber: supplier.vatNumber || '',
        fiscalCode: supplier.fiscalCode || '',
        address: supplier.address || '',
        city: supplier.city || '',
        postalCode: supplier.postalCode || '',
        province: supplier.province || '',
        country: supplier.country || 'Italia'
      };
      setSupplierDialogInitialData(dialogData);
      setOpenSupplierDialog(true);
    }
      const itemsWithTotal = (parsedItems || []).map((row) => {
        const qty = parseFloat(row.quantity) || 1;
        const up = parseFloat(row.unitPrice) || 0;
        const disc = parseFloat(row.discountPercent) || 0;
        const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
        return {
          productId: row.productId ?? null,
          productCode: row.productCode ?? '',
          description: row.description ?? '',
          quantity: row.quantity ?? 1,
          unitPrice: row.unitPrice ?? 0,
          discountPercent: row.discountPercent ?? 0,
          ivaPercent: (row.ivaPercent != null && row.ivaPercent !== '') ? parseFloat(row.ivaPercent) : DEFAULT_IVA,
          total: imp,
          isGeneralExpense: row.isGeneralExpense === true
        };
      });
      setItems(itemsWithTotal.length > 0 ? itemsWithTotal : [{ productId: null, productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, ivaPercent: DEFAULT_IVA, total: 0, isGeneralExpense: false }]);
    setImportXmlSuccess(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportXml = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setWizardError(null);
    setImportXmlSuccess(false);
    const isP7m = /\.p7m$/i.test(file.name || '');
    let xmlString = null;
    if (isP7m) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/purchase-invoices/extract-p7m', formData);
        xmlString = res.data?.xml;
        if (!xmlString || typeof xmlString !== 'string') {
          setWizardError('Il server non ha restituito XML dal file .p7m.');
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      } catch (err) {
        setWizardError(err.response?.data?.msg || err.message || 'Estrazione .p7m non riuscita.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    } else {
      const reader = new FileReader();
      await new Promise((resolve, reject) => {
        reader.onload = (ev) => {
          const text = ev.target?.result;
          if (typeof text !== 'string') {
            setWizardError('Impossibile leggere il file.');
            reject();
            return;
          }
          xmlString = text;
          resolve();
        };
        reader.onerror = () => {
          setWizardError('Errore nella lettura del file.');
          reject();
        };
        reader.readAsText(file, 'UTF-8');
      }).catch(() => {
        if (fileInputRef.current) fileInputRef.current.value = '';
      });
      if (xmlString == null) return;
    }
    const result = parseFatturaPAXml(xmlString);
    if (!result.success) {
      setWizardError(result.error || 'Import non riuscito.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    applyParsedInvoice(result.supplier, result.document, result.items);
  };

  const handleSupplierSearch = () => {
    if (!supplierSearch.trim()) {
      setSupplierSearchResults(suppliers);
      return;
    }
    const q = supplierSearch.trim().toLowerCase();
    setSupplierSearchResults(
      suppliers.filter(
        (s) =>
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.vatNumber && s.vatNumber.includes(q)) ||
          (s.fiscalCode && s.fiscalCode.includes(q))
      )
    );
  };

  const handleProductSearchByCode = (rowIndex, codeOverride) => {
    const codeToSearch = (codeOverride != null && codeOverride !== undefined) ? String(codeOverride).trim() : productSearchCode.trim();
    setProductSearchCode(codeToSearch);
    setProductSearchingRowIndex(rowIndex);
    setProductSearchMode('code');
    if (!codeToSearch) {
      setProductSearchResults([]);
      return;
    }
    api
      .get('/products', { params: { code: codeToSearch, limit: 20 } })
      .then((res) => {
        const list = res.data?.products ?? res.data ?? [];
        setProductSearchResults(Array.isArray(list) ? list : []);
      })
      .catch(() => setProductSearchResults([]));
  };

  const handleProductSearchByDescription = (rowIndex, textOverride) => {
    const textToSearch = (textOverride != null && textOverride !== undefined) ? String(textOverride).trim() : productSearchDesc.trim();
    setProductSearchDesc(textToSearch);
    setProductSearchingRowIndex(rowIndex);
    setProductSearchMode('desc');
    if (!textToSearch) {
      setProductSearchResults([]);
      return;
    }
    api
      .get('/products', { params: { searchNameDesc: textToSearch, limit: 20 } })
      .then((res) => {
        const list = res.data?.products ?? res.data ?? [];
        setProductSearchResults(Array.isArray(list) ? list : []);
      })
      .catch(() => setProductSearchResults([]));
  };

  const selectProduct = (rowIndex, product) => {
    const newItems = [...items];
    const qty = newItems[rowIndex].quantity || 1;
    const disc = newItems[rowIndex].discountPercent || 0;
    const up = parseFloat(newItems[rowIndex].unitPrice) || 0;
    const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
    newItems[rowIndex] = {
      ...newItems[rowIndex],
      productId: product.id,
      productCode: product.code,
      description: product.name || product.code,
      unitPrice: up,
      quantity: qty,
      discountPercent: disc,
      total: imp
    };
    setItems(newItems);
    setProductSearchResults([]);
    setProductSearchCode('');
    setProductSearchDesc('');
    setProductSearchingRowIndex(null);
    setProductSearchMode(null);
  };

  const selectSupplier = (supplier) => {
    setFormData((prev) => ({ ...prev, supplierId: supplier.id }));
    setSupplierSearch(supplier.name);
    setSupplierSearchResults([]);
  };

  const handleNewSupplierSave = (savedSupplier) => {
    fetchSuppliers();
    selectSupplier(savedSupplier);
    setOpenSupplierDialog(false);
    setSupplierDialogInitialData(null);
  };

  const getRowIvaPercent = (row) => (row.ivaPercent != null && row.ivaPercent !== '' && !Number.isNaN(parseFloat(row.ivaPercent))) ? Number(row.ivaPercent) : DEFAULT_IVA;

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    const qty = parseFloat(field === 'quantity' ? value : newItems[index].quantity) || 0;
    const up = parseFloat(field === 'unitPrice' ? value : newItems[index].unitPrice) || 0;
    const disc = parseFloat(field === 'discountPercent' ? value : newItems[index].discountPercent) || 0;
    const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
    newItems[index].total = imp;
    setItems(newItems);
  };

  const handleArticleCodeBlur = (idx) => {
    if (productSearchingRowIndex !== idx || productSearchMode !== 'code') return;
    const code = productSearchCode.trim();
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], productCode: code, productId: code === '' ? null : next[idx].productId };
      const qty = parseFloat(next[idx].quantity) || 0;
      const up = parseFloat(next[idx].unitPrice) || 0;
      const disc = parseFloat(next[idx].discountPercent) || 0;
      const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
      next[idx].total = imp;
      return next;
    });
    setProductSearchResults([]);
    setProductSearchingRowIndex(null);
    setProductSearchMode(null);
  };

  const handleArticleDescBlur = (idx) => {
    if (productSearchingRowIndex !== idx || productSearchMode !== 'desc') return;
    const desc = productSearchDesc.trim();
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], description: desc };
      const qty = parseFloat(next[idx].quantity) || 0;
      const up = parseFloat(next[idx].unitPrice) || 0;
      const disc = parseFloat(next[idx].discountPercent) || 0;
      const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
      next[idx].total = imp;
      return next;
    });
    setProductSearchResults([]);
    setProductSearchingRowIndex(null);
    setProductSearchMode(null);
    setProductSearchDesc('');
  };

  const clearRowProduct = (idx) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], productId: null, productCode: '' };
      const qty = parseFloat(next[idx].quantity) || 0;
      const up = parseFloat(next[idx].unitPrice) || 0;
      const disc = parseFloat(next[idx].discountPercent) || 0;
      const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
      next[idx].total = imp;
      return next;
    });
  };

  const defaultIvaForNewRow = (formData.ivaPercent !== '' && formData.ivaPercent != null && !Number.isNaN(parseFloat(formData.ivaPercent))) ? parseFloat(formData.ivaPercent) : DEFAULT_IVA;
  const addRow = () => {
    setItems((prev) => [...prev, { productId: null, productCode: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, ivaPercent: defaultIvaForNewRow, total: 0, isGeneralExpense: false }]);
  };

  const removeRow = (index) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenPendingImportDialog = async () => {
    setOpenPendingImportDialog(true);
    setPendingImportLoading(true);
    setPendingImportSelections({});
    try {
      const res = await api.get('/orders/pending-items');
      const list = Array.isArray(res.data) ? res.data : [];
      setPendingItemsList(list);
    } catch (err) {
      setError(err.response?.data?.message || 'Errore caricamento articoli da ricevere');
      setPendingItemsList([]);
    } finally {
      setPendingImportLoading(false);
    }
  };

  const handleClosePendingImportDialog = () => {
    setOpenPendingImportDialog(false);
    setPendingItemsList([]);
    setPendingImportSelections({});
  };

  const setPendingImportQty = (key, value) => {
    const num = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setPendingImportSelections((prev) => {
      const next = { ...prev };
      if (num === '' || num === 0) {
        delete next[key];
        return next;
      }
      next[key] = { quantityToImport: num };
      return next;
    });
  };

  const handleConfirmPendingImport = () => {
    const defaultIva = (formData.ivaPercent !== '' && formData.ivaPercent != null && !Number.isNaN(parseFloat(formData.ivaPercent))) ? parseFloat(formData.ivaPercent) : DEFAULT_IVA;
    const newRows = [];
    for (const item of pendingItemsList) {
      const qtyToReceive = (item.quantity || 0) - (item.receivedQuantity || 0);
      if (qtyToReceive <= 0) continue;
      const key = `${item.orderId}-${item.code}`;
      const sel = pendingImportSelections[key];
      if (!sel || sel.quantityToImport <= 0) continue;
      const qty = Math.min(sel.quantityToImport, qtyToReceive);
      const up = parseFloat(item.price) || 0;
      const imp = Math.round(qty * up * 100) / 100;
      const iva = Math.round(imp * (defaultIva / 100) * 100) / 100;
      newRows.push({
        productId: null,
        productCode: item.code || '',
        description: item.description || item.code || '',
        quantity: qty,
        unitPrice: up,
        discountPercent: 0,
        ivaPercent: defaultIva,
        total: imp + iva,
        isGeneralExpense: false,
        pendingOrderId: item.orderId,
        pendingOrderItemCode: item.code,
        sellingPrice: parseFloat(item.suggestedPrice) || 0,
        image: (item.image && String(item.image).trim()) || ''
      });
    }
    if (newRows.length > 0) {
      setItems((prev) => (prev.length === 1 && !prev[0].productCode && !prev[0].description ? newRows : [...prev, ...newRows]));
    }
    handleClosePendingImportDialog();
  };

  let totaleImponibile = 0;
  let totaleIva = 0;
  items.forEach((row) => {
    const qty = parseFloat(row.quantity) || 0;
    const up = parseFloat(row.unitPrice) || 0;
    const disc = parseFloat(row.discountPercent) || 0;
    const rowIvaPct = getRowIvaPercent(row);
    const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
    const iva = Math.round(imp * (rowIvaPct / 100) * 100) / 100;
    totaleImponibile += imp;
    totaleIva += iva;
  });
  const totalAmount = Math.round((totaleImponibile + totaleIva) * 100) / 100;
  totaleImponibile = Math.round(totaleImponibile * 100) / 100;
  totaleIva = Math.round(totaleIva * 100) / 100;

  const findMissingProductIndexes = () => {
    const missing = [];
    items.forEach((row, idx) => {
      if (row.isGeneralExpense) return; // spese generali non richiedono articolo in magazzino
      const code = (row.productCode || '').trim();
      const desc = (row.description || '').trim();
      if ((code || desc) && !row.productId) missing.push(idx);
    });
    return missing;
  };

  const findFirstPriceConflict = async (resolvedItemsToCheck, startAfterIndex = -1) => {
    if (formData.skipWarehouseMovement) return null;
    for (let idx = startAfterIndex + 1; idx < resolvedItemsToCheck.length; idx++) {
      const row = resolvedItemsToCheck[idx];
      if (row.isGeneralExpense || !row.productId) continue;
      const qty = parseFloat(row.quantity) || 0;
      if (qty <= 0) continue;
      try {
        const res = await api.get(`/products/${row.productId}`);
        if (!res.data || !res.data.id) continue;
        const invoicePrice = parseFloat(row.unitPrice) || 0;
        const warehousePrice = parseFloat(res.data.purchasePrice) || 0;
        if (Math.abs(invoicePrice - warehousePrice) > 0.01) {
          return { rowIndex: idx, row, product: res.data, resolvedItems: [...resolvedItemsToCheck] };
        }
      } catch (_) {
        // prodotto non trovato, salta
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!formData.supplierId || !formData.invoiceNumber || !formData.invoiceDate) {
      setError('Compilare fornitore, numero e data fattura');
      return;
    }
    const paidErr = validatePaidFields();
    if (paidErr) {
      setWizardError(paidErr);
      setActiveWizardStep(1);
      return;
    }
    setError(null);
    let resolvedItems = [...items];
    const missingIndexes = findMissingProductIndexes();
    // Se ci sono righe con codice ma senza productId, prova a risolvere per codice (articolo già in magazzino)
    if (missingIndexes.length > 0 && !formData.skipWarehouseMovement) {
      for (const idx of missingIndexes) {
        const row = resolvedItems[idx];
        const code = (row.productCode || '').trim();
        if (!code) continue;
        try {
          const res = await api.get(`/products/code/${encodeURIComponent(code)}`);
          if (res.data && res.data.id) {
            const invoicePrice = parseFloat(row.unitPrice) || 0;
            const warehousePrice = parseFloat(res.data.purchasePrice) || 0;
            const qty = parseFloat(row.quantity) || 0;
            if (qty > 0 && Math.abs(invoicePrice - warehousePrice) > 0.01) {
              setPriceConflictOnRegister({ rowIndex: idx, row, product: res.data, resolvedItems: [...resolvedItems] });
              return;
            }
            resolvedItems[idx] = {
              ...row,
              productId: res.data.id,
              productCode: res.data.code,
              description: res.data.name || row.description
            };
          }
        } catch (_) {
          // 404 o errore: prodotto non trovato, resterà "mancante"
        }
      }
      setItems(resolvedItems);
      const stillMissing = [];
      resolvedItems.forEach((row, idx) => {
        if (row.isGeneralExpense) return;
        const c = (row.productCode || '').trim();
        const d = (row.description || '').trim();
        if ((c || d) && !row.productId) stillMissing.push(idx);
      });
      if (stillMissing.length > 0) {
        setPendingSaveAfterProducts(true);
        setPendingMissingProductIndex(stillMissing[0]);
        setOpenNewProductDialog(true);
        return;
      }
      const conflict = await findFirstPriceConflict(resolvedItems);
      if (conflict) {
        setPriceConflictOnRegister(conflict);
        return;
      }
      await doSubmit(resolvedItems);
      return;
    }
    if (!formData.skipWarehouseMovement) {
      const conflict = await findFirstPriceConflict(resolvedItems);
      if (conflict) {
        setPriceConflictOnRegister(conflict);
        return;
      }
    }
    await doSubmit();
  };

  const openPriceConflictSellingStep = (action) => {
    if (!priceConflictOnRegister) return;
    const { product, row } = priceConflictOnRegister;
    const oldPurchase = parseFloat(product.purchasePrice) || 0;
    const oldSelling = parseFloat(product.sellingPrice) || 0;
    const newPurchase = parseFloat(row.unitPrice) || 0;
    const qty = parseFloat(row.quantity) || 0;
    const stock = parseInt(product.stock, 10) || 0;
    let proposedSelling = oldSelling;
    if (action === 'average' && oldPurchase > 0 && (stock + qty) > 0) {
      const weightedPurchase = (stock * oldPurchase + qty * newPurchase) / (stock + qty);
      const markup = oldSelling / oldPurchase;
      proposedSelling = Math.round(weightedPurchase * markup * 100) / 100;
    } else if (action === 'force') {
      const markup = oldPurchase > 0 ? oldSelling / oldPurchase : 1.22;
      proposedSelling = Math.round(newPurchase * markup * 100) / 100;
    }
    setPriceConflictSellingStep({ action, oldSellingPrice: oldSelling, proposedSellingPrice: proposedSelling });
    setEditableSellingPriceInDialog(proposedSelling.toFixed(2));
  };

  const handlePriceConflictResolve = async (option, updateSelling, customSellingPrice) => {
    if (!priceConflictOnRegister) return;
    const { rowIndex, row, product, resolvedItems } = priceConflictOnRegister;
    setPriceConflictLoading(true);
    setError(null);
    try {
      const productId = product.id;
      const newPurchasePrice = parseFloat(row.unitPrice) || 0;
      const quantityLoaded = parseFloat(row.quantity) || 0;
      const oldSelling = parseFloat(product.sellingPrice) || 0;
      const proposed = priceConflictSellingStep?.proposedSellingPrice;
      const effectiveSelling = (updateSelling && Number.isFinite(parseFloat(customSellingPrice)))
        ? parseFloat(customSellingPrice)
        : (updateSelling ? (proposed ?? Math.round(newPurchasePrice * (oldSelling / (parseFloat(product.purchasePrice) || 1)) * 100) / 100) : oldSelling);
      let productToUse = product;
      if (option === 'average') {
        await api.post('/products/update-prices-on-load', {
          productId,
          newPurchasePrice,
          quantityLoaded,
          action: 'average'
        });
        const res = await api.get(`/products/${productId}`);
        productToUse = res.data;
        if (!updateSelling) {
          await api.put(`/products/${productId}`, { ...productToUse, sellingPrice: oldSelling });
          const refetch = await api.get(`/products/${productId}`);
          productToUse = refetch.data;
        } else if (Number.isFinite(effectiveSelling) && Math.abs(effectiveSelling - parseFloat(productToUse.sellingPrice)) > 0.001) {
          await api.put(`/products/${productId}`, { ...productToUse, sellingPrice: effectiveSelling });
          const refetch = await api.get(`/products/${productId}`);
          productToUse = refetch.data;
        }
      } else if (option === 'force') {
        const newSelling = updateSelling ? (Number.isFinite(effectiveSelling) ? effectiveSelling : oldSelling) : oldSelling;
        await api.put(`/products/${productId}`, {
          ...product,
          purchasePrice: newPurchasePrice,
          sellingPrice: newSelling
        });
        const res = await api.get(`/products/${productId}`);
        productToUse = res.data;
      }
      const nextResolved = [...resolvedItems];
      nextResolved[rowIndex] = {
        ...row,
        productId: productToUse.id,
        productCode: productToUse.code,
        description: productToUse.name || row.description
      };
      setItems(nextResolved);
      setPriceConflictOnRegister(null);
      setPriceConflictSellingStep(null);
      setEditableSellingPriceInDialog('');
      const nextConflict = await findFirstPriceConflict(nextResolved, rowIndex);
      if (nextConflict) {
        setPriceConflictOnRegister(nextConflict);
      } else {
        await doSubmit(nextResolved);
      }
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.msg || errData?.error || err.message || 'Errore durante l\'aggiornamento prezzi');
    } finally {
      setPriceConflictLoading(false);
    }
  };

  const handlePriceConflictLeaveUnchanged = async () => {
    if (!priceConflictOnRegister) return;
    const { rowIndex, row, product, resolvedItems } = priceConflictOnRegister;
    setPriceConflictLoading(true);
    setError(null);
    try {
      const nextResolved = [...resolvedItems];
      nextResolved[rowIndex] = {
        ...row,
        productId: product.id,
        productCode: product.code,
        description: product.name || row.description
      };
      setItems(nextResolved);
      setPriceConflictOnRegister(null);
      const nextConflict = await findFirstPriceConflict(nextResolved, rowIndex);
      if (nextConflict) {
        setPriceConflictOnRegister(nextConflict);
      } else {
        await doSubmit(nextResolved);
      }
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.msg || errData?.error || err.message || 'Errore durante il salvataggio');
    } finally {
      setPriceConflictLoading(false);
    }
  };

  const handleNewProductSave = (savedProduct) => {
    if (pendingMissingProductIndex == null) return;
    const newItems = [...items];
    newItems[pendingMissingProductIndex] = {
      ...newItems[pendingMissingProductIndex],
      productId: savedProduct.id,
      productCode: savedProduct.code,
      description: savedProduct.name
    };
    setItems(newItems);
    setOpenNewProductDialog(false);
    const remaining = [];
    newItems.forEach((row, idx) => {
      if (row.isGeneralExpense) return;
      const code = (row.productCode || '').trim();
      const desc = (row.description || '').trim();
      if ((code || desc) && !row.productId) remaining.push(idx);
    });
    if (remaining.length > 0 && pendingSaveAfterProducts) {
      setPendingMissingProductIndex(remaining[0]);
      setOpenNewProductDialog(true);
    } else {
      setPendingMissingProductIndex(null);
      setPendingSaveAfterProducts(false);
      doSubmit(newItems);
    }
  };

  const doSubmit = async (itemsToUse = null) => {
    const list = itemsToUse != null ? itemsToUse : items;
    const asBozza = formData.saveAsBozza === true;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        paid: formData.paid === true,
        paymentDate: formData.paid ? (formData.paymentDate || formData.invoiceDate) : null,
        paymentMethodId: formData.paymentMethodId || null,
        accountId: formData.accountId || null,
        ivaPercent: (formData.ivaPercent === '' || formData.ivaPercent == null) ? DEFAULT_IVA : Number(formData.ivaPercent),
        saveAsBozza: asBozza,
        items: list.map((r) => {
          const qty = parseFloat(r.quantity) || 0;
          const up = parseFloat(r.unitPrice) || 0;
          const disc = parseFloat(r.discountPercent) || 0;
          const rowIvaPct = getRowIvaPercent(r);
          const imp = Math.round(qty * up * (1 - disc / 100) * 100) / 100;
          const iva = Math.round(imp * (rowIvaPct / 100) * 100) / 100;
          return {
            productId: r.productId || null,
            productCode: (r.productCode || '').trim() || null,
            description: (r.description || '').trim() || null,
            quantity: r.quantity,
            unitPrice: r.unitPrice,
            discountPercent: parseFloat(r.discountPercent) || 0,
            ivaPercent: rowIvaPct,
            total: imp + iva,
            isGeneralExpense: r.isGeneralExpense === true,
            pendingOrderId: r.pendingOrderId || null,
            pendingOrderItemCode: r.pendingOrderItemCode || null
          };
        })
      };
      if (editingInvoice) {
        await api.put(`/purchase-invoices/${editingInvoice.id}`, payload);
        if (!asBozza && editingInvoice.status === 'bozza') {
          try {
            await api.post(`/purchase-invoices/${editingInvoice.id}/register`);
          } catch (regErr) {
            setError(regErr.response?.data?.msg || 'Fattura salvata ma registrazione magazzino non effettuata.');
          }
        }
      } else {
        const created = await api.post('/purchase-invoices', payload);
        const invoiceId = created.data?.id;
        if (invoiceId && !asBozza) {
          try {
            await api.post(`/purchase-invoices/${invoiceId}/register`);
          } catch (regErr) {
            setError(regErr.response?.data?.msg || 'Fattura salvata ma registrazione magazzino non effettuata.');
          }
        }
      }
      fetchInvoices();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleRegister = async (inv) => {
    // Invece di registrare \"alla cieca\" solo sul backend, apro la fattura in modifica
    // così vengono gestiti TUTTI gli articoli con la stessa logica del wizard:
    // - articoli mancanti -> dialog Nuovo articolo in magazzino per ciascuno
    // - conflitti di prezzo -> dialog per ogni articolo con prezzo diverso.
    try {
      const res = await api.get(`/purchase-invoices/${inv.id}`);
      handleOpenEdit(res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore durante il caricamento della fattura per la registrazione');
    }
  };

  const validatePaidFields = () => {
    if (!formData.paid) return null;
    const missing = [];
    if (!formData.paymentDate || String(formData.paymentDate).trim() === '') missing.push('data pagamento');
    if (!formData.paymentMethodId) missing.push('metodo di pagamento');
    if (!formData.accountId) missing.push('conto');
    if (missing.length === 0) return null;
    return `Fattura segnata come pagata: compilare i campi obbligatori (${missing.join(', ')}).`;
  };

  const handleWizardNext = async () => {
    setWizardError(null);
    if (activeWizardStep === 1) {
      const numTrim = String(formData.invoiceNumber || '').trim();
      if (!formData.supplierId || !numTrim) {
        setWizardError('Inserire fornitore e numero fattura prima di procedere.');
        return;
      }
      const paidErr = validatePaidFields();
      if (paidErr) {
        setWizardError(paidErr);
        return;
      }
      try {
        const params = new URLSearchParams({
          supplierId: formData.supplierId,
          invoiceNumber: numTrim
        });
        if (editingInvoice?.id) params.set('excludeId', editingInvoice.id);
        const res = await api.get(`/purchase-invoices/check-duplicate?${params.toString()}`);
        if (res.data?.duplicate) {
          setWizardError('Esiste già una fattura con lo stesso fornitore e numero documento. Inserire un numero diverso.');
          return;
        }
      } catch (err) {
        setWizardError(err.response?.data?.msg || 'Errore durante la verifica.');
        return;
      }
    }
    setActiveWizardStep((s) => s + 1);
  };

  const handleViewInvoice = async (inv) => {
    try {
      const res = await api.get(`/purchase-invoices/${inv.id}`);
      setViewInvoiceDetail(res.data);
      setOpenViewDialog(true);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore caricamento dettaglio fattura');
    }
  };

  const handleDeleteClick = (inv) => {
    setInvoiceToDelete(inv);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      await api.delete(`/purchase-invoices/${invoiceToDelete.id}`);
      fetchInvoices();
      setOpenDeleteDialog(false);
      setInvoiceToDelete(null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore eliminazione');
    }
  };

  const handleOpenPayDialog = (inv) => {
    setInvoiceToPay(inv);
    setPayForm({
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethodId: '',
      accountId: ''
    });
    setOpenPayDialog(true);
  };

  const handleConfirmPay = async () => {
    if (!invoiceToPay || !payForm.paymentDate || !payForm.paymentMethodId || !payForm.accountId) {
      setError('Inserire data pagamento, metodo di pagamento e conto');
      return;
    }
    setError(null);
    try {
      const body = {
        paymentDate: payForm.paymentDate,
        paymentMethodId: payForm.paymentMethodId,
        accountId: payForm.accountId
      };
      await api.post(`/purchase-invoices/${invoiceToPay.id}/pay`, body);
      fetchInvoices();
      setOpenPayDialog(false);
      setInvoiceToPay(null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore durante il salvataggio del pagamento');
    }
  };

  const currentMissingRow = pendingMissingProductIndex != null ? items[pendingMissingProductIndex] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Fatture passive</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={showFilters ? 'Nascondi filtri' : 'Mostra filtri'}>
            <IconButton onClick={() => setShowFilters((v) => !v)} color={showFilters ? 'primary' : 'default'} size="small" aria-label={showFilters ? 'Nascondi filtri' : 'Mostra filtri'}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
            Nuova fattura
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'invoiceNumber' ? order : false}>
                    <TableSortLabel active={orderBy === 'invoiceNumber'} direction={orderBy === 'invoiceNumber' ? order : 'asc'} onClick={() => handleRequestSort('invoiceNumber')}>
                      N. fattura
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'invoiceDate' ? order : false}>
                    <TableSortLabel active={orderBy === 'invoiceDate'} direction={orderBy === 'invoiceDate' ? order : 'asc'} onClick={() => handleRequestSort('invoiceDate')}>
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'documentType' ? order : false}>
                    <TableSortLabel active={orderBy === 'documentType'} direction={orderBy === 'documentType' ? order : 'asc'} onClick={() => handleRequestSort('documentType')}>
                      Tipologia
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'supplierName' ? order : false}>
                    <TableSortLabel active={orderBy === 'supplierName'} direction={orderBy === 'supplierName' ? order : 'asc'} onClick={() => handleRequestSort('supplierName')}>
                      Fornitore
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={orderBy === 'totalAmount' ? order : false}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <TableSortLabel active={orderBy === 'totalAmount'} direction={orderBy === 'totalAmount' ? order : 'asc'} onClick={() => handleRequestSort('totalAmount')}>
                        Totale
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell>Scadenza</TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleRequestSort('status')}>
                      Stato
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
                {showFilters && (
                <TableRow>
                  <TableCell padding="none" sx={{ verticalAlign: 'bottom' }}>
                    <TextField size="small" placeholder="Filtra" value={listFilters.invoiceNumber} onChange={(e) => setListFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }))} sx={{ minWidth: 90 }} inputProps={{ 'aria-label': 'Filtra N. fattura' }} />
                  </TableCell>
                  <TableCell padding="none" sx={{ verticalAlign: 'bottom' }}>
                    <TextField size="small" placeholder="Filtra" value={listFilters.invoiceDate} onChange={(e) => setListFilters((prev) => ({ ...prev, invoiceDate: e.target.value }))} sx={{ minWidth: 90 }} inputProps={{ 'aria-label': 'Filtra Data' }} />
                  </TableCell>
                  <TableCell padding="none" sx={{ verticalAlign: 'bottom' }}>
                    <TextField size="small" placeholder="Filtra" value={listFilters.documentType} onChange={(e) => setListFilters((prev) => ({ ...prev, documentType: e.target.value }))} sx={{ minWidth: 90 }} inputProps={{ 'aria-label': 'Filtra Tipologia' }} />
                  </TableCell>
                  <TableCell padding="none" sx={{ verticalAlign: 'bottom' }}>
                    <TextField size="small" placeholder="Filtra" value={listFilters.supplierName} onChange={(e) => setListFilters((prev) => ({ ...prev, supplierName: e.target.value }))} sx={{ minWidth: 100 }} inputProps={{ 'aria-label': 'Filtra Fornitore' }} />
                  </TableCell>
                  <TableCell padding="none" align="right" sx={{ verticalAlign: 'bottom' }}>
                    <TextField size="small" placeholder="Min €" value={listFilters.totalAmount} onChange={(e) => setListFilters((prev) => ({ ...prev, totalAmount: e.target.value }))} sx={{ minWidth: 70 }} inputProps={{ 'aria-label': 'Filtra Totale' }} />
                  </TableCell>
                  <TableCell padding="none" />
                  <TableCell padding="none" sx={{ verticalAlign: 'bottom' }}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select displayEmpty value={listFilters.status} onChange={(e) => setListFilters((prev) => ({ ...prev, status: e.target.value }))} inputProps={{ 'aria-label': 'Filtra Stato' }}>
                        <MenuItem value="">Tutti</MenuItem>
                        <MenuItem value="bozza">Bozza</MenuItem>
                        <MenuItem value="pagata">Pagata</MenuItem>
                        <MenuItem value="da pagare">Da pagare</MenuItem>
                        <MenuItem value="insoluta">Insoluta</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell padding="none" />
                </TableRow>
                )}
              </TableHead>
              <TableBody>
                {displayedInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {invoices.length === 0 ? 'Nessuna fattura. Clicca "Nuova fattura" per registrare una fattura di acquisto.' : 'Nessun risultato con i filtri impostati.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedInvoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoiceNumber}</TableCell>
                      <TableCell>{inv.invoiceDate ? format(parseISO(inv.invoiceDate), 'dd/MM/yyyy', { locale: it }) : '-'}</TableCell>
                      <TableCell>{inv.documentType || '-'}</TableCell>
                      <TableCell>{inv.Supplier?.name || '-'}</TableCell>
                      <TableCell align="right">€{parseFloat(inv.totalAmount || 0).toFixed(2)}</TableCell>
                      <TableCell>{inv.paid ? '' : (inv.dueDate ? format(parseISO(inv.dueDate), 'dd/MM/yyyy', { locale: it }) : '-')}</TableCell>
                      <TableCell>
                        <Chip size="small" {...getListStatusChip(inv)} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Visualizza">
                          <IconButton size="small" onClick={() => handleViewInvoice(inv)} color="inherit">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {(inv.status === 'bozza' || inv.status === 'registrata') && (
                          <>
                            <Tooltip title="Modifica">
                              <IconButton size="small" onClick={() => handleOpenEdit(inv)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {inv.status === 'bozza' && (
                              <Tooltip title="Registra (movimento di magazzino)">
                                <IconButton size="small" onClick={() => handleRegister(inv)}>
                                  <RegisterIcon color="primary" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Elimina">
                              <IconButton size="small" onClick={() => handleDeleteClick(inv)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            {inv.status === 'registrata' && !inv.paid && (
                              <Tooltip title="Segna come pagata (data e movimento contabile)">
                                <IconButton size="small" onClick={() => handleOpenPayDialog(inv)} color="primary">
                                  <EuroIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '75vh', maxHeight: '90vh' } }}>
        <DialogTitle>{editingInvoice ? 'Modifica fattura' : 'Nuova fattura passiva'}</DialogTitle>
        <DialogContent>
          {wizardError && (
            <Alert severity="error" onClose={() => setWizardError(null)} sx={{ mb: 2 }}>
              {wizardError}
            </Alert>
          )}
          <Stepper activeStep={activeWizardStep} sx={{ pt: 1, pb: 3 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Fornitore */}
          {activeWizardStep === 0 && (
            <Box sx={{ minHeight: 220 }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                Seleziona o crea il fornitore
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xml,.p7m,application/xml,text/xml"
                  onChange={handleImportXml}
                  style={{ display: 'none' }}
                />
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()} size="medium" sx={{ whiteSpace: 'nowrap' }}>
                  Importa da XML / .p7m (FatturaPA)
                </Button>
              </Box>
              {importXmlSuccess && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => setImportXmlSuccess(false)}>
                  Dati fattura importati. Verifica fornitore, numero e data, poi prosegui con Avanti.
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Cerca per nome, P.IVA, cod. fiscale..."
                  value={supplierSearch}
                  onChange={(e) => setSupplierSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setSupplierSearchResults([]), 200)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSupplierSearch())}
                  sx={{
                    '& .MuiInputBase-input': { color: '#212121' },
                    '& .MuiInputBase-input::placeholder': { color: '#757575', opacity: 1 }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {supplierSearch && (
                          <IconButton size="small" onClick={() => setSupplierSearch('')} aria-label="Cancella" sx={{ mr: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                        <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleSupplierSearch}>Cerca</Button>
                      </InputAdornment>
                    )
                  }}
                />
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => { setSupplierDialogInitialData(null); setOpenSupplierDialog(true); }} sx={{ whiteSpace: 'nowrap' }}>
                  Nuovo fornitore
                </Button>
              </Box>
              {supplierSearchResults.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1.5, maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {supplierSearchResults.map((s) => (
                      <ListItem key={s.id} button onClick={() => selectSupplier(s)}>
                        <ListItemText
                          primary={s.name}
                          secondary={s.vatNumber || s.fiscalCode || ''}
                          sx={{
                            '& .MuiListItemText-primary': { color: '#212121 !important' },
                            '& .MuiListItemText-secondary': { color: '#212121 !important' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
              {formData.supplierId && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => { setFormData((p) => ({ ...p, supplierId: '' })); setSupplierSearch(''); }}>
                  Fornitore selezionato
                </Alert>
              )}
            </Box>
          )}

          {/* Step 2: Documento */}
          {activeWizardStep === 1 && (
            <Box sx={{ minHeight: 220 }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                Dati documento
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.saveAsBozza === true}
                    onChange={(e) => setFormData((p) => ({ ...p, saveAsBozza: e.target.checked }))}
                    disabled={editingInvoice?.status === 'registrata'}
                  />
                }
                label="Bozza (salva senza movimento di magazzino)"
                sx={{ display: 'block', mb: 1 }}
              />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="N. fattura *" value={formData.invoiceNumber} onChange={(e) => setFormData((p) => ({ ...p, invoiceNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Data fattura *" type="date" InputLabelProps={{ shrink: true }} value={formData.invoiceDate} onChange={(e) => setFormData((p) => ({ ...p, invoiceDate: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo doc.</InputLabel>
                    <Select value={formData.documentType} label="Tipo doc." onChange={(e) => setFormData((p) => ({ ...p, documentType: e.target.value }))}>
                      {DOCUMENT_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formData.paid}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((p) => {
                            if (!checked) return { ...p, paid: false };
                            const next = { ...p, paid: true };
                            const invDate = p.invoiceDate;
                            if (invDate) {
                              if (!p.paymentDate) next.paymentDate = invDate;
                              if (!p.dueDate) next.dueDate = invDate;
                            }
                            return next;
                          });
                        }}
                      />
                    )}
                    label="Pagata"
                  />
                  {formData.paid && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      Obbligatori: data pagamento, metodo di pagamento e conto.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={formData.paid ? 'Data pagamento *' : 'Scadenza pagamento'}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.paid ? formData.paymentDate : formData.dueDate}
                    onChange={(e) => setFormData((p) => ({ ...p, [p.paid ? 'paymentDate' : 'dueDate']: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="IVA %" type="number" inputProps={{ min: 0, max: 100, step: 0.01 }} value={formData.ivaPercent === '' || formData.ivaPercent == null ? '' : formData.ivaPercent} onChange={(e) => setFormData((p) => ({ ...p, ivaPercent: e.target.value === '' ? '' : (parseFloat(e.target.value) ?? 0) }))} placeholder="22" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{formData.paid ? 'Metodo pagamento *' : 'Metodo pagamento'}</InputLabel>
                    <Select value={formData.paymentMethodId || ''} label={formData.paid ? 'Metodo pagamento *' : 'Metodo pagamento'} onChange={(e) => setFormData((p) => ({ ...p, paymentMethodId: e.target.value || null }))}>
                      <MenuItem value="">—</MenuItem>
                      {paymentMethods.map((pm) => (
                        <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{formData.paid ? 'Conto *' : 'Conto'}</InputLabel>
                    <Select value={formData.accountId || ''} label={formData.paid ? 'Conto *' : 'Conto'} onChange={(e) => setFormData((p) => ({ ...p, accountId: e.target.value || null }))}>
                      <MenuItem value="">—</MenuItem>
                      {accounts.map((a) => (
                        <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Note" multiline rows={2} value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))} />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Articoli */}
          {activeWizardStep === 2 && (
            <Box sx={{ minHeight: 220 }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                Righe articoli
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.skipWarehouseMovement === true}
                    onChange={(e) => setFormData((p) => ({ ...p, skipWarehouseMovement: e.target.checked }))}
                  />
                }
                label="Non creare movimento di magazzino (alla registrazione)"
                sx={{ display: 'block', mb: 1.5 }}
              />
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 56 }}>
                        <Tooltip title="Spese generali: non movimenta magazzino, salvate solo come spese">
                          <span>Spese gen.</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>Codice</TableCell>
                      <TableCell>Descrizione</TableCell>
                      <TableCell align="center" width={70}>Qtà</TableCell>
                      <TableCell align="right" width={90}>€ unit.</TableCell>
                      <TableCell align="center" width={70}>Sconto %</TableCell>
                      <TableCell align="center" width={70}>IVA %</TableCell>
                      <TableCell align="right" width={90}>Imponibile</TableCell>
                      <TableCell width={48}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
                          <Checkbox
                            size="small"
                            checked={row.isGeneralExpense === true}
                            onChange={(e) => updateItem(idx, 'isGeneralExpense', e.target.checked)}
                            title="Spese generali: non movimenta magazzino"
                          />
                        </TableCell>
                        <TableCell sx={{ position: 'relative' }}>
                          <Box ref={productSearchingRowIndex === idx ? codeFieldAnchorRef : null} sx={{ display: 'inline-block' }}>
                            <TextField
                              size="small"
                              placeholder="Codice"
                              value={productSearchingRowIndex === idx ? productSearchCode : (row.productCode || '')}
                              onChange={(e) => {
                                if (productSearchingRowIndex === idx) {
                                  const v = e.target.value;
                                  setProductSearchCode(v);
                                  if (v === '') clearRowProduct(idx);
                                } else {
                                  updateItem(idx, 'productCode', e.target.value);
                                }
                              }}
                              onFocus={() => {
                                if (productSearchingRowIndex !== idx || productSearchMode !== 'code') {
                                  setProductSearchingRowIndex(idx);
                                  setProductSearchCode(row.productCode || '');
                                  setProductSearchMode('code');
                                }
                              }}
                              onBlur={(e) => {
                                if (e.relatedTarget && popoverContentRef.current?.contains(e.relatedTarget)) return;
                                setTimeout(() => handleArticleCodeBlur(idx), 150);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleProductSearchByCode(idx, productSearchingRowIndex === idx ? productSearchCode : (row.productCode || '')))}
                              sx={{ width: 180, '& .MuiInputBase-input': { color: '#212121' } }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleProductSearchByCode(idx, productSearchingRowIndex === idx ? productSearchCode : (row.productCode || ''))} aria-label="Cerca">
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Box>
                          <Popover
                            open={productSearchingRowIndex === idx && productSearchResults.length > 0}
                            anchorEl={productSearchMode === 'desc' ? descFieldAnchorRef.current : codeFieldAnchorRef.current}
                            onClose={() => { setProductSearchResults([]); setProductSearchingRowIndex(null); setProductSearchMode(null); setProductSearchDesc(''); }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ sx: { maxHeight: 220, minWidth: 260 } }}
                            disableAutoFocus
                            disableEnforceFocus
                          >
                            <Box ref={popoverContentRef} onMouseDown={(e) => e.preventDefault()}>
                            <List dense>
                              {productSearchResults.map((p) => (
                                <ListItem key={p.id} button onClick={() => selectProduct(productSearchingRowIndex, p)} onMouseDown={(e) => e.preventDefault()}>
                                  <ListItemText
                                    primary={`${p.code} - ${p.name}`}
                                    secondary={`€ ${p.purchasePrice || 0}`}
                                    sx={{
                                      '& .MuiListItemText-primary': { color: '#212121' },
                                      '& .MuiListItemText-secondary': { color: '#212121' }
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                            </Box>
                          </Popover>
                        </TableCell>
                        <TableCell>
                          <Box ref={productSearchingRowIndex === idx && productSearchMode === 'desc' ? descFieldAnchorRef : null} sx={{ display: 'inline-block', width: '100%', minWidth: 360 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Descrizione"
                              value={productSearchingRowIndex === idx && productSearchMode === 'desc' ? productSearchDesc : (row.description || '')}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (productSearchingRowIndex === idx && productSearchMode === 'desc') {
                                  setProductSearchDesc(v);
                                } else {
                                  updateItem(idx, 'description', v);
                                }
                              }}
                              onFocus={() => {
                                if (productSearchingRowIndex !== idx || productSearchMode !== 'desc') {
                                  setProductSearchingRowIndex(idx);
                                  setProductSearchMode('desc');
                                  setProductSearchDesc(row.description || '');
                                }
                              }}
                              onBlur={(e) => {
                                if (e.relatedTarget && popoverContentRef.current?.contains(e.relatedTarget)) return;
                                setTimeout(() => handleArticleDescBlur(idx), 150);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleProductSearchByDescription(idx, productSearchingRowIndex === idx && productSearchMode === 'desc' ? productSearchDesc : (row.description || '')))}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleProductSearchByDescription(idx, productSearchingRowIndex === idx && productSearchMode === 'desc' ? productSearchDesc : (row.description || ''))} aria-label="Cerca per nome/descrizione">
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                              sx={{ minWidth: 360 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 0.001 }} value={row.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" inputProps={{ min: 0, step: 0.01 }} value={row.unitPrice} onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)} sx={{ width: 90 }} />
                        </TableCell>
                        <TableCell align="center">
                          <TextField type="number" size="small" inputProps={{ min: 0, max: 100, step: 0.01 }} value={row.discountPercent} onChange={(e) => updateItem(idx, 'discountPercent', e.target.value)} sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell align="center">
                          <TextField type="number" size="small" inputProps={{ min: 0, max: 100, step: 0.01 }} value={row.ivaPercent != null && row.ivaPercent !== '' ? row.ivaPercent : ''} onChange={(e) => updateItem(idx, 'ivaPercent', e.target.value === '' ? '' : (parseFloat(e.target.value) ?? 22))} placeholder="22" sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell align="right">€{(parseFloat(row.total) || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeRow(idx)} disabled={items.length <= 1}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ mt: 1 }}>
                Aggiungi riga
              </Button>
              <Button size="small" startIcon={<Inventory2Icon />} onClick={handleOpenPendingImportDialog} sx={{ mt: 1, ml: 1 }} variant="outlined">
                Importa da articoli da ricevere
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mt: 2 }}>
                <Typography variant="body2">Imponibile: €{totaleImponibile.toFixed(2)} — IVA: €{totaleIva.toFixed(2)}</Typography>
                <Typography variant="subtitle1" fontWeight={600}>Totale: €{totalAmount.toFixed(2)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Box sx={{ flex: 1 }} />
          {activeWizardStep > 0 && (
            <Button onClick={() => setActiveWizardStep((s) => s - 1)}>Indietro</Button>
          )}
          {activeWizardStep < 2 ? (
            <Button
              variant="contained"
              onClick={handleWizardNext}
              disabled={activeWizardStep === 0 && !formData.supplierId}
            >
              Avanti
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!priceConflictOnRegister} onClose={() => { if (!priceConflictLoading) { setPriceConflictOnRegister(null); setPriceConflictSellingStep(null); setEditableSellingPriceInDialog(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>{priceConflictSellingStep ? 'Modifica prezzo di vendita' : 'Variazione prezzo di acquisto'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {priceConflictSellingStep ? (
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom>
                Vuoi aggiornare anche il prezzo di vendita?
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Prezzo di vendita attuale: <b>€{priceConflictSellingStep.oldSellingPrice.toFixed(2)}</b>
              </Typography>
              <TextField
                fullWidth
                label="Nuovo prezzo di vendita"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={editableSellingPriceInDialog}
                onChange={(e) => setEditableSellingPriceInDialog(e.target.value)}
                sx={{ mb: 2 }}
                disabled={priceConflictLoading}
              />
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => handlePriceConflictResolve(priceConflictSellingStep.action, true, editableSellingPriceInDialog)}
                variant="contained"
                color="primary"
                disabled={priceConflictLoading}
              >
                {priceConflictLoading ? <CircularProgress size={20} /> : 'Sì, aggiorna anche il prezzo di vendita'}
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => handlePriceConflictResolve(priceConflictSellingStep.action, false)}
                variant="outlined"
                disabled={priceConflictLoading}
              >
                No, lascia invariato il prezzo di vendita
              </Button>
            </Box>
          ) : priceConflictOnRegister && (
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom>
                L&apos;articolo <b>{priceConflictOnRegister.product.code}</b> ha in fattura un prezzo di acquisto (<b>€{(parseFloat(priceConflictOnRegister.row.unitPrice) || 0).toFixed(2)}</b>) diverso da quello attualmente in magazzino (<b>€{(parseFloat(priceConflictOnRegister.product.purchasePrice) || 0).toFixed(2)}</b>).
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Se altri articoli hanno prezzo diverso dal magazzino, ti verrà chiesto come procedere per ognuno.
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Vuoi aggiornare il prezzo di acquisto (e eventualmente il prezzo di vendita)?
              </Typography>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => openPriceConflictSellingStep('average')}
                variant="contained"
                color="primary"
                disabled={priceConflictLoading}
              >
                Sì, aggiorna con la media ponderata
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => openPriceConflictSellingStep('force')}
                variant="contained"
                disabled={priceConflictLoading}
              >
                Sì, usa il prezzo della fattura
              </Button>
              <Button
                fullWidth
                sx={{ mt: 1 }}
                onClick={handlePriceConflictLeaveUnchanged}
                variant="outlined"
                color="secondary"
                disabled={priceConflictLoading}
              >
                No, lascia i prezzi invariati e registra solo il carico
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {priceConflictSellingStep ? (
            <Button onClick={() => { setPriceConflictSellingStep(null); setEditableSellingPriceInDialog(''); }} disabled={priceConflictLoading}>
              Indietro
            </Button>
          ) : null}
          <Button onClick={() => { setPriceConflictOnRegister(null); setPriceConflictSellingStep(null); setEditableSellingPriceInDialog(''); }} disabled={priceConflictLoading}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      <SupplierDialog open={openSupplierDialog} onClose={() => { setOpenSupplierDialog(false); setSupplierDialogInitialData(null); }} onSave={handleNewSupplierSave} initialData={supplierDialogInitialData} />

      <NewProductFromInvoiceDialog
        open={openNewProductDialog}
        onClose={() => {
          setOpenNewProductDialog(false);
          setPendingMissingProductIndex(null);
          setPendingSaveAfterProducts(false);
        }}
        onSave={handleNewProductSave}
        initialCode={currentMissingRow?.productCode || ''}
        initialName={currentMissingRow?.description || ''}
        initialPurchasePrice={currentMissingRow?.unitPrice || 0}
        initialSellingPrice={currentMissingRow?.sellingPrice}
        initialImage={currentMissingRow?.image}
      />

      <Dialog open={openPayDialog} onClose={() => setOpenPayDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Segna come pagata</DialogTitle>
        <DialogContent>
          {invoiceToPay && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fattura {invoiceToPay.invoiceNumber} – €{parseFloat(invoiceToPay.totalAmount || 0).toFixed(2)}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Data pagamento *"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={payForm.paymentDate}
                onChange={(e) => setPayForm((p) => ({ ...p, paymentDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Metodo pagamento *</InputLabel>
                <Select
                  value={payForm.paymentMethodId}
                  label="Metodo pagamento *"
                  onChange={(e) => setPayForm((p) => ({ ...p, paymentMethodId: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {paymentMethods.map((pm) => (
                    <MenuItem key={pm.id} value={pm.id}>{pm.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Conto *</InputLabel>
                <Select
                  value={payForm.accountId}
                  label="Conto *"
                  onChange={(e) => setPayForm((p) => ({ ...p, accountId: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayDialog(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleConfirmPay}
            disabled={!payForm.paymentDate || !payForm.paymentMethodId || !payForm.accountId}
          >
            Conferma pagamento
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Elimina fattura</DialogTitle>
        <DialogContent>
          Eliminare la fattura {invoiceToDelete?.invoiceNumber}?
          {invoiceToDelete?.status === 'registrata' && (
            <> Verranno eliminati anche il movimento contabile e i movimenti di magazzino collegati. Continuare?</>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openViewDialog} onClose={() => { setOpenViewDialog(false); setViewInvoiceDetail(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Dettaglio fattura</DialogTitle>
        <DialogContent>
          {viewInvoiceDetail && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Fornitore</Typography>
                <Typography variant="body1">{viewInvoiceDetail.Supplier?.name || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">N. fattura</Typography>
                <Typography variant="body1">{viewInvoiceDetail.invoiceNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Data</Typography>
                <Typography variant="body1">{viewInvoiceDetail.invoiceDate ? format(parseISO(viewInvoiceDetail.invoiceDate), 'dd/MM/yyyy', { locale: it }) : '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Tipo doc.</Typography>
                <Typography variant="body1">{viewInvoiceDetail.documentType || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Stato</Typography>
                <Typography variant="body1">{viewInvoiceDetail.status === 'registrata' ? 'Registrata' : 'Bozza'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Pagata</Typography>
                <Typography variant="body1">{viewInvoiceDetail.paid ? 'Sì' : 'No'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">{viewInvoiceDetail.paid ? 'Data pagamento' : 'Scadenza'}</Typography>
                <Typography variant="body1">
                  {(viewInvoiceDetail.paid ? viewInvoiceDetail.paymentDate : viewInvoiceDetail.dueDate)
                    ? format(parseISO(viewInvoiceDetail.paid ? viewInvoiceDetail.paymentDate : viewInvoiceDetail.dueDate), 'dd/MM/yyyy', { locale: it })
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">IVA %</Typography>
                <Typography variant="body1">{viewInvoiceDetail.ivaPercent != null ? viewInvoiceDetail.ivaPercent : '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Non crea mov. magazzino</Typography>
                <Typography variant="body1">{viewInvoiceDetail.skipWarehouseMovement ? 'Sì' : 'No'}</Typography>
              </Grid>
              {(viewInvoiceDetail.PaymentMethod || viewInvoiceDetail.Account) && (
                <>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">Metodo pagamento</Typography>
                    <Typography variant="body1">{viewInvoiceDetail.PaymentMethod?.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">Conto</Typography>
                    <Typography variant="body1">{viewInvoiceDetail.Account?.name || '-'}</Typography>
                  </Grid>
                </>
              )}
              {viewInvoiceDetail.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Note</Typography>
                  <Typography variant="body1">{viewInvoiceDetail.notes}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>Righe articoli</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Spese</TableCell>
                        <TableCell>Codice</TableCell>
                        <TableCell>Descrizione</TableCell>
                        <TableCell align="center">Qtà</TableCell>
                        <TableCell align="right">€ unit.</TableCell>
                        <TableCell align="center">Sconto %</TableCell>
                        <TableCell align="center">IVA %</TableCell>
                        <TableCell align="right">Totale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewInvoiceDetail.PurchaseInvoiceItems || []).map((item, i) => (
                        <TableRow key={i}>
                          <TableCell padding="checkbox">{item.isGeneralExpense ? 'Sì' : '-'}</TableCell>
                          <TableCell>{item.productCode || (item.Product?.code || '-')}</TableCell>
                          <TableCell>{item.description || (item.Product?.name || '-')}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">{parseFloat(item.unitPrice || 0).toFixed(2)}</TableCell>
                          <TableCell align="center">{parseFloat(item.discountPercent || 0).toFixed(2)}</TableCell>
                          <TableCell align="center">{item.ivaPercent != null ? parseFloat(item.ivaPercent).toFixed(2) : '-'}</TableCell>
                          <TableCell align="right">€{parseFloat(item.total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Totale: €{parseFloat(viewInvoiceDetail.totalAmount || 0).toFixed(2)}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenViewDialog(false); setViewInvoiceDetail(null); }}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPendingImportDialog} onClose={handleClosePendingImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>Importa da articoli da ricevere (Shop B2B)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleziona gli articoli da aggiungere alla fattura. Alla registrazione della fattura le quantità verranno scalate da &quot;Articoli da ricevere&quot;.
          </Typography>
          {pendingImportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingItemsList.length === 0 ? (
            <Alert severity="info">Nessun articolo da ricevere. Gli ordini in attesa con quantità non ancora ricevute appariranno qui.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Importa</TableCell>
                    <TableCell>Codice</TableCell>
                    <TableCell>Descrizione</TableCell>
                    <TableCell align="center">Da ricevere</TableCell>
                    <TableCell align="center">Qtà da importare</TableCell>
                    <TableCell align="right">€ unit.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingItemsList.map((item) => {
                    const qtyToReceive = (item.quantity || 0) - (item.receivedQuantity || 0);
                    if (qtyToReceive <= 0) return null;
                    const key = `${item.orderId}-${item.code}`;
                    const sel = pendingImportSelections[key];
                    const qtyImport = sel ? sel.quantityToImport : 0;
                    return (
                      <TableRow key={key}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={qtyImport > 0}
                            onChange={(e) => setPendingImportQty(key, e.target.checked ? qtyToReceive : 0)}
                          />
                        </TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.description || '-'}</TableCell>
                        <TableCell align="center">{qtyToReceive}</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: qtyToReceive, step: 1 }}
                            value={qtyImport || ''}
                            onChange={(e) => setPendingImportQty(key, e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell align="right">€{parseFloat(item.price || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePendingImportDialog}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleConfirmPendingImport}
            disabled={pendingImportLoading || Object.keys(pendingImportSelections).length === 0 || Object.values(pendingImportSelections).every((s) => !s.quantityToImport || s.quantityToImport <= 0)}
          >
            Importa selezionati
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingPurchaseInvoicesPage;
